import axios from 'axios';
import { api } from './api';

export type SlaCountType = 'Running' | 'Business';

export interface SlaDto {
  id: string;
  customerId: string;
  calendarId?: string | null;
  name: string;
  description?: string | null;
  timeInMinutes: number;
  alertPercentage: number;
  countType: SlaCountType;
  priorityRule?: string | null;
}

export interface CreateSlaDto {
  customerId: string;
  calendarId?: string | null;
  name: string;
  description?: string | null;
  timeInMinutes: number;
  alertPercentage: number;
  countType: SlaCountType;
  priorityRule?: string | null;
}

export interface UpdateSlaDto {
  customerId: string;
  calendarId?: string | null;
  name: string;
  description?: string | null;
  timeInMinutes: number;
  alertPercentage: number;
  countType: SlaCountType;
  priorityRule?: string | null;
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const payload = data as Record<string, unknown>;
      if (typeof payload.detail === 'string') return payload.detail;
      if (typeof payload.title === 'string') return payload.title;
      if (typeof payload.message === 'string') return payload.message;
    }
    if (error.response?.statusText) {
      return `${error.response.status}: ${error.response.statusText}`;
    }
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

class SlaService {
  async getSlas(customerId: string): Promise<SlaDto[]> {
    try {
      const response = await api.get<SlaDto[]>('/slas', { params: { customerId } });
      return response.data ?? [];
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao buscar SLAs.'));
    }
  }

  async getSlaById(id: string): Promise<SlaDto | null> {
    try {
      const response = await api.get<SlaDto>(`/slas/${id}`);
      return response.data ?? null;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw new Error(extractApiErrorMessage(error, 'Erro ao buscar SLA.'));
    }
  }

  async createSla(data: CreateSlaDto): Promise<string> {
    try {
      const response = await api.post<string>('/slas', data);
      return typeof response.data === 'string' ? response.data : String(response.data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao criar SLA.'));
    }
  }

  async updateSla(id: string, data: UpdateSlaDto): Promise<void> {
    try {
      await api.put(`/slas/${id}`, data);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao atualizar SLA.'));
    }
  }

  async deleteSla(id: string): Promise<void> {
    try {
      await api.delete(`/slas/${id}`);
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Erro ao excluir SLA.'));
    }
  }
}

export const slaService = new SlaService();
